import { useEffect, useState, type HTMLAttributes } from 'react'
import { cn } from '../lib/cn'
import { formatRelative } from '../lib/format'

/**
 * "salvo há 2 minutos" — o formulário longo diz quando salvou, em texto discreto ao lado do botão.
 * Timestamp de log ("Salvo: 05/09/2026 18:52:26") nunca aparece na interface. Quando o salvar
 * falhou, diz a causa em plum e oferece "tentar de novo" (o autosave de uma ficha).
 */
export interface SavedIndicatorProps extends HTMLAttributes<HTMLSpanElement> {
  savedAt: Date | null
  /** Enquanto salva. */
  saving?: boolean
  /** Alterações ainda não salvas. */
  dirty?: boolean
  /** O salvar falhou: a causa, em plum. Vence os demais estados. */
  error?: string | null
  /** Com `error`, a saída: "tentar de novo". */
  onRetry?: () => void
  retryLabel?: string
}

export function SavedIndicator({
  savedAt,
  saving,
  dirty,
  error,
  onRetry,
  retryLabel = 'tentar de novo',
  className,
  ...props
}: SavedIndicatorProps) {
  const [, tick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 30_000)
    return () => clearInterval(t)
  }, [])

  let text: string | null = null
  if (error) text = error
  else if (saving) text = 'salvando'
  else if (dirty) text = 'alterações não salvas'
  else if (savedAt) text = `salvo ${formatRelative(savedAt)}`

  if (!text) return null
  return (
    <span
      data-slot="saved-indicator"
      role="status"
      aria-live="polite"
      className={cn(
        'font-body text-eyebrow font-light tracking-normal',
        error || (dirty && !saving) ? 'text-plum' : 'text-label',
        className,
      )}
      {...props}
    >
      {text}
      {error && onRetry ? (
        <>
          {' — '}
          <button
            type="button"
            onClick={onRetry}
            className="border-plum text-plum hover:border-ink hover:text-ink cursor-pointer border-b pb-px"
          >
            {retryLabel}
          </button>
        </>
      ) : null}
    </span>
  )
}
